import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { startRegistration } from '@simplewebauthn/browser';
import { Card, Button, Alert, Typography, Space, Spin, Result, Descriptions } from 'antd';
import { KeyOutlined, CheckCircleOutlined, SafetyCertificateOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { validateResetToken, getResetRegisterOptions, resetPasskey } from '../lib/api';

const { Title, Text } = Typography;

export default function ResetPasskey() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [isValidating, setIsValidating] = useState(true);
  const [isValid, setIsValid] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState<'info' | 'register' | 'success'>('info');
  const [userId, setUserId] = useState<number | null>(null);
  const [userInfo, setUserInfo] = useState<{ email?: string; username?: string; name?: string } | null>(null);

  useEffect(() => {
    if (!token) {
      setIsValidating(false);
      setValidationError('缺少重置連結憑證');
      return;
    }

    validateResetToken(token)
      .then((data) => {
        setIsValidating(false);
        if (data.valid) {
          setIsValid(true);
          setUserId(data.userId ?? null);
          setUserInfo(data.user ?? null);
        } else {
          setValidationError(data.error || '無效的重置連結');
        }
      })
      .catch(() => {
        setIsValidating(false);
        setValidationError('驗證連結時發生錯誤');
      });
  }, [token]);

  const handleStartReset = () => {
    setStep('register');
  };

  const handleRegisterPasskey = async () => {
    if (!userId || !token) return;

    setLoading(true);
    setError('');

    try {
      const options = await getResetRegisterOptions(userId, userInfo?.email || '', token);

      const challengeId = options.challengeId;
      const { challengeId: _, ...webauthnOptions } = options;

      const attestationResponse = await startRegistration(webauthnOptions);

      await resetPasskey({
        token,
        userId,
        challengeId,
        response: attestationResponse,
      });

      setStep('success');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (isValidating) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f5f5' }}>
        <Card>
          <div style={{ textAlign: 'center' }}>
            <Spin size="large" />
            <Text style={{ display: 'block', marginTop: 16 }}>驗證中...</Text>
          </div>
        </Card>
      </div>
    );
  }

  if (!isValid) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f5f5', padding: 24 }}>
        <Card style={{ width: '100%', maxWidth: 420 }}>
          <Alert
            title="無效的連結"
            description={
              <Space orientation="vertical">
                <Text>{validationError}</Text>
                <Text>此重置連結可能已過期或已被使用。</Text>
                <Text>請聯繫管理員取得新的重置連結。</Text>
                <Link to="/login">
                  <Button type="primary">返回登入頁面</Button>
                </Link>
              </Space>
            }
            type="error"
            showIcon
          />
        </Card>
      </div>
    );
  }

  if (step === 'success') {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f5f5', padding: 24 }}>
        <Card style={{ width: '100%', maxWidth: 420 }}>
          <Result
            status="success"
            icon={<CheckCircleOutlined />}
            title="重置完成！"
            subTitle="您的 Passkey 已成功重置。舊的 Passkey 已自動失效。"
            extra={[
              <Link to="/login" key="login">
                <Button type="primary">前往登入</Button>
              </Link>,
            ]}
          />
        </Card>
      </div>
    );
  }

  if (step === 'register') {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', padding: 24 }}>
        <Card style={{ width: '100%', maxWidth: 420, boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}>
          <Space orientation="vertical" style={{ width: '100%' }} size="large">
            <div style={{ textAlign: 'center' }}>
              <SafetyCertificateOutlined style={{ fontSize: 64, color: '#1890ff', marginBottom: 16 }} />
              <Title level={3} style={{ margin: 0 }}>設定新 Passkey</Title>
              <Text type="secondary">請設定新的無密碼登入憑證</Text>
            </div>

            {error && <Alert title={error} type="error" showIcon closable />}

            <Alert
              title="重置說明"
              description="您將設定新的 Passkey 來取代舊的憑證。重置後，舊的 Passkey 將無法使用。"
              type="info"
              showIcon
            />

            <Button
              type="primary"
              size="large"
              block
              onClick={handleRegisterPasskey}
              loading={loading}
            >
              設定新 Passkey
            </Button>
          </Space>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', padding: 24 }}>
      <Card style={{ width: '100%', maxWidth: 420, boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}>
        <Space orientation="vertical" style={{ width: '100%' }} size="large">
          <div style={{ textAlign: 'center' }}>
            <KeyOutlined style={{ fontSize: 64, color: '#1890ff', marginBottom: 16 }} />
            <Title level={3} style={{ margin: 0 }}>重置 Passkey</Title>
            <Text type="secondary">帳號救援流程</Text>
          </div>

          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label="目標帳號">
              {userInfo?.name || userInfo?.username || userInfo?.email || 'Unknown User'}
            </Descriptions.Item>
            {userInfo?.email && <Descriptions.Item label="Email">{userInfo.email}</Descriptions.Item>}
            {userInfo?.username && <Descriptions.Item label="帳號">{userInfo.username}</Descriptions.Item>}
          </Descriptions>

          <Alert
            title="重要提醒"
            description={
              <ul style={{ paddingLeft: 20, margin: '8px 0' }}>
                <li>此連結為一次性使用，重置後將自動失效</li>
                <li>新 Passkey 設定完成後，舊的 Passkey 將無法使用</li>
                <li>請確保您正在為正確的帳號設定新的 Passkey</li>
              </ul>
            }
            type="warning"
            showIcon
            icon={<ExclamationCircleOutlined />}
          />

          <Button
            type="primary"
            size="large"
            block
            onClick={handleStartReset}
          >
            開始重置 Passkey
          </Button>

          <div style={{ textAlign: 'center' }}>
            <Text type="secondary">不是您要的帳號？ <Link to="/login">返回登入</Link></Text>
          </div>
        </Space>
      </Card>
    </div>
  );
}
